import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from '../contexts/AuthContext';
import { Calendar, TrendingUp, Award } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';

interface AttendanceRecord {
  id: string;
  session_id: string;
  attended: boolean;
  created_at: string;
  training_sessions: {
    title: string;
    date: string;
    start_time: string;
    instructor_id: string | null;
  };
}

const ParticipationHistory = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [participationData, setParticipationData] = useState<AttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchParticipationData();
    }
  }, [user]);

  const fetchParticipationData = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('session_attendance')
        .select(`
          *,
          training_sessions (
            title,
            date,
            start_time,
            instructor_id
          )
        `)
        .eq('student_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching participation data:', error);
        return;
      }

      setParticipationData(data || []);
    } catch (error) {
      console.error('Error fetching participation data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateMonthlyStats = () => {
    const monthlyData: { [key: string]: { attended: number; missed: number } } = {};

    participationData.forEach(record => {
      const date = new Date(record.training_sessions.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { attended: 0, missed: 0 };
      }

      if (record.attended) {
        monthlyData[monthKey].attended++;
      } else {
        monthlyData[monthKey].missed++;
      }
    });

    return Object.entries(monthlyData)
      .map(([key, stats]) => {
        const [year, month] = key.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString(currentLocale(), {
          month: 'long',
          year: 'numeric'
        });
        const total = stats.attended + stats.missed;
        const rate = total > 0 ? Math.round((stats.attended / total) * 100) : 0;

        return {
          month: monthName,
          attended: stats.attended,
          missed: stats.missed,
          rate
        };
      })
      .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime())
      .slice(0, 4);
  };

  if (isLoading) {
    return (
      <Layout title={t("common:navigation.participationHistory")}>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-900 mx-auto mb-4"></div>
              <p>{t("common:messages.loading")}</p>
            </div>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const totalSessions = participationData.length;
  const attendedSessions = participationData.filter(p => p.attended).length;
  const attendanceRate = totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0;
  const monthlyStats = calculateMonthlyStats();

  const achievements = [
    { title: uiText("Training Dedication"), description: uiText("Completed {{value0}} training sessions", { value0: attendedSessions }), date: new Date().toLocaleDateString(currentLocale()) },
    { title: uiText("Consistent Attendance"), description: uiText("{{value0}}% attendance rate", { value0: attendanceRate }), date: new Date().toLocaleDateString(currentLocale()) },
    { title: uiText("Active Member"), description: uiText("{{value0}} total sessions recorded", { value0: totalSessions }), date: new Date().toLocaleDateString(currentLocale()) },
  ];

  return (
    <Layout title={t("common:navigation.participationHistory")}>
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-900">{attendanceRate}%</p>
                  <p className="text-sm text-gray-600">{uiText("Overall Attendance")}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-900">{attendedSessions}</p>
                  <p className="text-sm text-gray-600">{uiText("Sessions Attended")}</p>
                </div>
                <Calendar className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-900">{achievements.length}</p>
                  <p className="text-sm text-gray-600">{uiText("Achievements")}</p>
                </div>
                <Award className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Participation */}
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Recent Training Sessions")}</CardTitle>
              <CardDescription>{uiText("Your latest participation records")}</CardDescription>
            </CardHeader>
            <CardContent>
              {participationData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>{uiText("No participation records found.")}</p>
                  <p className="text-sm mt-2">{uiText("Start attending training sessions to see your progress here.")}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {participationData.slice(0, 10).map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{record.training_sessions.title}</p>
                        <p className="text-sm text-gray-600">{new Date(record.training_sessions.date).toLocaleDateString(currentLocale())}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(`1970-01-01T${record.training_sessions.start_time}`).toLocaleTimeString(currentLocale(), {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Badge variant={record.attended ? 'default' : 'destructive'}>
                        {record.attended ? uiText("Attended") : uiText("Missed")}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Monthly Statistics")}</CardTitle>
              <CardDescription>{uiText("Your attendance trends over time")}</CardDescription>
            </CardHeader>
            <CardContent>
              {monthlyStats.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{uiText("No monthly statistics available yet.")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {monthlyStats.map((month, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-gray-900">{month.month}</h4>
                        <Badge variant={month.rate >= 80 ? 'default' : 'secondary'}>
                          {month.rate}%
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{uiText("Attended:")}{' '}{month.attended}</span>
                        <span>{uiText("Missed:")}{' '}{month.missed}</span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-red-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${month.rate}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Achievements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              {uiText("Achievements")}</CardTitle>
            <CardDescription>{uiText("Your training milestones and accomplishments")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {achievements.map((achievement, index) => (
                <div key={index} className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-900 rounded-full">
                      <Award className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-red-900">{achievement.title}</h4>
                      <p className="text-sm text-red-700">{achievement.description}</p>
                      <p className="text-xs text-red-600 mt-1">{achievement.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Progress Insights */}
        <Card>
          <CardHeader>
            <CardTitle>{uiText("Training Insights")}</CardTitle>
            <CardDescription>{uiText("Analysis of your training patterns")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{uiText("Strengths")}</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  {attendanceRate >= 80 && <li>{uiText("• Excellent attendance rate")}</li>}
                  {attendedSessions >= 10 && <li>{uiText("• Consistent training dedication")}</li>}
                  {totalSessions >= 5 && <li>{uiText("• Active participation in sessions")}</li>}
                  <li>{uiText("• Committed to martial arts journey")}</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{uiText("Areas for Improvement")}</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  {attendanceRate < 80 && <li>{uiText("• Work on maintaining consistent attendance")}</li>}
                  {attendedSessions < 10 && <li>{uiText("• Attend more training sessions")}</li>}
                  <li>{uiText("• Set monthly attendance goals")}</li>
                  <li>{uiText("• Consider additional training opportunities")}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ParticipationHistory;
